// src/pages/admin/AdminVerifications.js
// Admin : vérifications KYC des marchands
// Maxula

import React, { useEffect, useState, useCallback } from 'react';
import { FiCheck, FiX, FiFileText, FiExternalLink } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { verificationAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';

const STATUS = {
  pending: 'bg-yellow-100 text-yellow-700',
  verified: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  none: 'bg-gray-100 text-gray-600'
};
const LABELS = { pending: 'En attente', verified: 'Vérifié', rejected: 'Refusé', none: '—' };
const FILTERS = ['pending', 'verified', 'rejected', ''];

const docLink = (f) => (f ? normalizeImagePath(f) : null);

const AdminVerifications = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async (st) => {
    try {
      setLoading(true);
      const res = await verificationAPI.adminList(st || undefined);
      setItems(res.data?.data || []);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const approve = async (m) => {
    try { setBusy(m._id); await verificationAPI.approve(m._id); toast.success('Marchand vérifié'); load(filter); }
    catch (e) { toast.error('Erreur'); } finally { setBusy(null); }
  };
  const reject = async (m) => {
    const reason = window.prompt('Motif du refus ?', 'Documents non conformes');
    if (reason === null) return;
    try { setBusy(m._id); await verificationAPI.reject(m._id, reason); toast.success('KYC refusé'); load(filter); }
    catch (e) { toast.error('Erreur'); } finally { setBusy(null); }
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Vérifications (KYC)</h1>
      <p className="text-gray-500 mb-4">Validez les documents des marchands.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f || 'all'} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${filter === f ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}>
            {f ? LABELS[f] : 'Tous'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div>
      ) : items.length === 0 ? (
        <div className="text-center text-gray-400 py-12">Aucune demande.</div>
      ) : (
        <div className="space-y-3">
          {items.map((m) => {
            const k = m.kyc || {};
            const docs = m.documents || {};
            return (
              <div key={m._id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-semibold text-gray-900">{m.businessName}
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${STATUS[k.status || 'none']}`}>{LABELS[k.status || 'none']}</span>
                    </div>
                    <div className="text-xs text-gray-400">{m.email} · {m.businessType} · {k.entityType === 'morale' ? 'Société (morale)' : 'Personne physique'}</div>
                  </div>
                  <div className="flex gap-2">
                    {k.status !== 'verified' && (
                      <button onClick={() => approve(m)} disabled={busy === m._id}
                        className="px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm flex items-center gap-1 hover:bg-green-700">
                        <FiCheck size={14} /> Valider
                      </button>
                    )}
                    {k.status !== 'rejected' && (
                      <button onClick={() => reject(m)} disabled={busy === m._id}
                        className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm flex items-center gap-1 hover:bg-red-700">
                        <FiX size={14} /> Refuser
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
                  <div><span className="text-gray-400">CIN:</span> {k.cinNumber || '—'}</div>
                  <div><span className="text-gray-400">RC:</span> {k.rcNumber || '—'}</div>
                  <div><span className="text-gray-400">ICE:</span> {k.iceNumber || '—'}</div>
                  <div><span className="text-gray-400">IF:</span> {k.ifNumber || '—'}</div>
                  <div><span className="text-gray-400">Patente:</span> {k.patenteNumber || '—'}</div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[['cin', 'CIN'], ['registreCommerce', 'RC'], ['identificationFiscale', 'ICE/IF'], ['patente', 'Patente']].map(([key, lbl]) => {
                    const url = docLink(docs[key]);
                    return url ? (
                      <a key={key} href={url} target="_blank" rel="noreferrer"
                        className="px-2 py-1 rounded border text-xs text-primary-600 inline-flex items-center gap-1 hover:bg-gray-50">
                        <FiFileText size={12} /> {lbl} <FiExternalLink size={10} />
                      </a>
                    ) : null;
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminVerifications;
